import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaWhatsapp, FaTimes } from 'react-icons/fa'

type Step = {
  id: string
  question: string
  placeholder: string
  type: 'text' | 'select'
  options?: { value: string; label: string }[]
}

const steps: Step[] = [
  {
    id: 'name',
    question: 'Olá! Como posso chamar você?',
    placeholder: 'Digite seu nome...',
    type: 'text',
  },
  {
    id: 'interest',
    question: 'Qual serviço você tem interesse?',
    placeholder: 'Selecione uma opção...',
    type: 'select',
    options: [
      { value: 'portoes', label: 'Portões' },
      { value: 'estruturas', label: 'Estruturas Metálicas' },
      { value: 'moveis', label: 'Móveis Industriais' },
      { value: 'manutencao', label: 'Manutenção' },
      { value: 'outros', label: 'Outros Serviços' },
    ],
  },
  {
    id: 'urgency',
    question: 'Qual a urgência do seu projeto?',
    placeholder: 'Selecione uma opção...',
    type: 'select',
    options: [
      { value: 'urgente', label: 'Urgente - Preciso para os próximos dias' },
      { value: 'medio', label: 'Médio prazo - Para as próximas semanas' },
      { value: 'planejamento', label: 'Em planejamento - Sem urgência' },
    ],
  },
  {
    id: 'details',
    question: 'Conte-nos mais sobre o seu projeto:',
    placeholder: 'Descreva os detalhes, medidas, materiais ou outras informações importantes...',
    type: 'text',
  },
]

export default function WhatsAppButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      const message = formatMessage()
      const whatsappNumber = '5515998225714'
      const encodedMessage = encodeURIComponent(message)
      window.open(
        `https://wa.me/${whatsappNumber}?text=${encodedMessage}`,
        '_blank'
      )
      setIsOpen(false)
      setCurrentStep(0)
      setAnswers({})
    }
  }

  const formatMessage = () => {
    return `Olá! Me chamo ${answers.name}.\n\n` +
           `Tenho interesse em: ${getOptionLabel('interest', answers.interest)}\n` +
           `Urgência: ${getOptionLabel('urgency', answers.urgency)}\n\n` +
           `Detalhes do projeto:\n${answers.details}`
  }

  const getOptionLabel = (stepId: string, value: string) => {
    const step = steps.find(s => s.id === stepId)
    return step?.options?.find(o => o.value === value)?.label || value
  }

  const currentStepData = steps[currentStep]

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        aria-label={isOpen ? 'Fechar chat' : 'Abrir chat do WhatsApp'}
      >
        {isOpen ? (
          <FaTimes className="h-6 w-6" />
        ) : (
          <FaWhatsapp className="h-7 w-7" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="absolute bottom-16 right-0 w-80 overflow-hidden rounded-lg bg-white shadow-xl"
          >
            <div className="bg-green-500 p-4 text-white">
              <h3 className="text-lg font-medium">Fale Conosco</h3>
              <p className="mt-1 text-sm opacity-90">
                {currentStep + 1} de {steps.length}
              </p>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              <div className="space-y-4 p-4">
                {steps.slice(0, currentStep + 1).map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2"
                  >
                    <p className="font-medium text-gray-700">{step.question}</p>
                    {index === currentStep && (
                      <form onSubmit={handleSubmit} className="space-y-2">
                        {step.type === 'select' ? (
                          <select
                            value={answers[step.id] || ''}
                            onChange={(e) =>
                              setAnswers({ ...answers, [step.id]: e.target.value })
                            }
                            className="w-full rounded-md border border-gray-300 p-2 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                            required
                          >
                            <option value="">{step.placeholder}</option>
                            {step.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={answers[step.id] || ''}
                            onChange={(e) =>
                              setAnswers({ ...answers, [step.id]: e.target.value })
                            }
                            placeholder={step.placeholder}
                            className="w-full rounded-md border border-gray-300 p-2 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                            required
                          />
                        )}
                        <button
                          type="submit"
                          className="w-full rounded-md bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                          {currentStep === steps.length - 1
                            ? 'Iniciar Conversa no WhatsApp'
                            : 'Continuar'}
                        </button>
                      </form>
                    )}
                    {index < currentStep && (
                      <p className="text-gray-600">
                        {step.type === 'select'
                          ? getOptionLabel(step.id, answers[step.id])
                          : answers[step.id]}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
} 